import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useDebounce } from '@/hooks/use-debounce';
import { useTranslation } from '@/hooks/useTranslation';

interface UseCompanyIdentifierResult {
  identifier: string;
  setIdentifier: (value: string) => void;
  isChecking: boolean;
  isAvailable: boolean | null;
  validationMessage: string;
}

export function useCompanyIdentifier(companyName: string, currentCompanyId?: string): UseCompanyIdentifierResult {
  const { t } = useTranslation('pages');
  const [identifier, setIdentifier] = useState('');
  const [isUserEdited, setIsUserEdited] = useState(false);
  const [isChecking, setIsChecking] = useState(false);
  const [isAvailable, setIsAvailable] = useState<boolean | null>(null);
  const [validationMessage, setValidationMessage] = useState('');

  // Debounce the identifier for API calls
  const debouncedIdentifier = useDebounce(identifier, 500);

  // Generate identifier from company name
  const generateIdentifier = useCallback((name: string): string => {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9]/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '');
  }, []);

  // Auto-generate identifier when company name changes (unless user has manually edited it)
  useEffect(() => {
    if (!isUserEdited && companyName.trim()) {
      const generated = generateIdentifier(companyName);
      setIdentifier(generated);
    }
  }, [companyName, generateIdentifier, isUserEdited]);

  // Check uniqueness when debounced identifier changes
  useEffect(() => {
    const checkUniqueness = async () => {
      if (!debouncedIdentifier.trim()) {
        setIsAvailable(null);
        setValidationMessage('');
        return;
      }

      if (debouncedIdentifier.length < 3) {
        setIsAvailable(false);
        setValidationMessage(t('company_management.identifier_min_length'));
        return;
      }

      setIsChecking(true);
      setValidationMessage(t('company_management.identifier_checking'));

      try {
        let query = supabase
          .from('company_profiles')
          .select('id')
          .eq('company_identifier', debouncedIdentifier as any);

        // Exclude current company from uniqueness check
        if (currentCompanyId) {
          query = query.neq('id', currentCompanyId as any);
        }

        const { data, error } = await query.maybeSingle();

        if (error) throw error;

        const available = !data;
        setIsAvailable(available);
        setValidationMessage(
          available 
            ? t('company_management.identifier_available')
            : t('company_management.identifier_taken')
        );
      } catch (error) {
        console.error('Error checking identifier uniqueness:', error);
        setIsAvailable(null);
        setValidationMessage(t('company_management.identifier_error'));
      } finally {
        setIsChecking(false);
      }
    };

    checkUniqueness();
  }, [debouncedIdentifier, currentCompanyId, t]);

  const handleSetIdentifier = useCallback((value: string) => {
    setIdentifier(value);
    setIsUserEdited(true);
  }, []);

  return {
    identifier,
    setIdentifier: handleSetIdentifier,
    isChecking,
    isAvailable,
    validationMessage
  };
}